import React, { useEffect, useState } from "react";
import { Box, Rating, Typography } from "@mui/material";
import AuthService from "../../services/AuthService";
import Logger from "../../services/Logger";
import UserService from "../../services/UserService";
import FeedbackService from "../puzzles/common/feedback/FeedbackService";
import { usePagesTranslation } from "../locales";

/**
 * 1–5 star rating for a story (logged-in users). Stored as puzzle feedback with storyId in details.
 */
export default function StoryRatingStars({ storyId }) {
    const { t } = usePagesTranslation();
    const [allowed, setAllowed] = useState(false);
    const [stars, setStars] = useState(0);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const authed = await AuthService.isAuthenticated();
                if (!cancelled) setAllowed(Boolean(authed));
            } catch {
                if (!cancelled) setAllowed(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [storyId]);

    if (!storyId || !allowed) return null;

    const handleChange = async (_event, next) => {
        const value = Number(next) || 0;
        if (value < 1) return;
        setStars(value);
        setSaving(true);
        try {
            const user = await UserService.getUserInfo();
            await FeedbackService.submitFeedback({
                cloudUserId: user?.cloudUser?.id,
                stars: value,
                details: { storyId, puzzleType: "STORY" },
            });
        } catch (error) {
            Logger.error("Story rating failed:", error);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, flexWrap: "wrap" }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t("storybook.rate-story", "Rate this story")}
            </Typography>
            <Rating
                name={`story-rating-${storyId}`}
                value={stars}
                onChange={handleChange}
                disabled={saving}
            />
        </Box>
    );
}
